/**
 * Create/Edit List Tag Rule modal
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Plus, X } from "lucide-react";

import { Button } from "@/components/ui";
import { Card } from "@/components/ui";
import { ColorPicker } from "@/components/ui";
import { CreatableCombobox } from "@/components/ui";
import { Input } from "@/components/ui";
import { Switch } from "@/components/ui";
import { useModalPortalSurface } from "@/components/ui/layout/ModalPortalSurfaceContext";
import type {
  CreateListTagRuleDto,
  ListTagRule,
  TagRuleCondition,
} from "@/hooks";
import { resolveEditableRange } from "@/components/date-range";
import { DateRangePresetDropdown } from "@/features/smswiser-campaigns/components/SmswiserCampaignStatsTab";
import {
  getRangeFromPreset,
  getPresetFromRange,
} from "@/features/smswiser-campaigns/components/SmswiserCampaignStatsTab";
import { cn } from "@/lib/utils";

import {
  isDefaultScope,
  useScopeLabels,
  type TagRuleScope,
} from "@/features/tag-rule-scope";
import { CompareWindowExclusions } from "@/features/tag-rule-compare";
import {
  HOUR_DEFAULT_MIN_VOLUME,
  HOUR_EXCLUDED_PRESETS,
  HourMinVolumeField,
  parseMinVolume,
  toHourPreset,
} from "@/features/hour-tags";
import { ConditionModeRow } from "./rule-condition";
import { ScopeBanner } from "./rule-condition/parts";
import {
  DROP_RULE_LIST_SYSTEM_TAGS,
  TAG_RULE_EMOJIS,
} from "@trafficpulse/shared";

import {
  DROP_LIST_METRIC_FIELDS,
  DROP_SOURCE_METRIC_FIELDS,
  LIST_METRIC_FIELDS,
  TAG_COLOR_PRESETS,
} from "../constants";

const NO_EXCLUDED_PRESETS: readonly string[] = [];

interface ListTagRuleFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initial?: ListTagRule | null;
  onSubmit: (dto: CreateListTagRuleDto) => Promise<void>;
  submitting: boolean;
  /** Existing group keys from rules — used for select-or-create UX */
  availableGroupKeys?: string[];
  /** Country/campaign in view; conditions edit THAT scope's override. */
  scope?: TagRuleScope;
  /** Rule variant — 'drop' additionally offers the source_* metric fields;
   *  'hour' adds the minimum visits and whole-day-only ranges. */
  variant?: string;
}

export const ListTagRuleFormModal: FC<ListTagRuleFormModalProps> = ({
  open,
  onOpenChange,
  initial,
  onSubmit,
  submitting,
  availableGroupKeys = [],
  scope,
  variant,
}) => {
  const isEdit = !!initial;
  const metricFields = useMemo(
    () =>
      variant === "drop"
        ? [
            ...LIST_METRIC_FIELDS,
            ...DROP_SOURCE_METRIC_FIELDS,
            ...DROP_LIST_METRIC_FIELDS,
          ]
        : LIST_METRIC_FIELDS,
    [variant],
  );
  const { labelFor } = useScopeLabels();
  // Inside the Flow Optimizer drawer (a modal Radix dialog) this must portal
  // INTO the drawer's element — a body portal sits outside its FocusScope,
  // which reclaims focus on every click/keystroke and the inputs go dead.
  const portalHost = useModalPortalSurface();
  const defaultRange = useMemo(() => getRangeFromPreset("last-30-days"), []);
  const [name, setName] = useState("");
  const [color, setColor] = useState<string>("");
  const [emoji, setEmoji] = useState<string>("");
  const [singleSourceOnly, setSingleSourceOnly] = useState(false);
  const [listSystemTags, setListSystemTags] = useState<string[]>([]);
  const [conditions, setConditions] = useState<TagRuleCondition[]>([
    { field: "profit", operator: "gte", value: 100 },
  ]);
  const [fromDate, setFromDate] = useState(defaultRange?.from ?? "");
  const [toDate, setToDate] = useState(defaultRange?.to ?? "");
  const [toDateIsToday, setToDateIsToday] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [groupKey, setGroupKey] = useState("");
  const [minVolume, setMinVolume] = useState(String(HOUR_DEFAULT_MIN_VOLUME));
  const isHour = variant === "hour";
  const minVolumeValue = parseMinVolume(minVolume);
  const excludedPresets = isHour ? HOUR_EXCLUDED_PRESETS : NO_EXCLUDED_PRESETS;

  useEffect(() => {
    if (open) {
      if (initial) {
        setName(initial.name);
        setColor(initial.color ?? "");
        setEmoji(initial.emoji ?? "");
        setSingleSourceOnly(initial.singleSourceOnly === true);
        setListSystemTags(initial.listSystemTags ?? []);
        setConditions(
          initial.conditions?.length
            ? [...initial.conditions]
            : [{ field: "profit", operator: "gte", value: 100 }],
        );
        // The stored from/to are only a snapshot of the preset at save time —
        // resolveEditableRange re-resolves a rolling preset like the evaluator does.
        const range = resolveEditableRange(initial);
        setFromDate(range.from);
        setToDate(range.to);
        setToDateIsToday(range.toDateIsToday);
        setEnabled(initial.enabled ?? true);
        setGroupKey(initial.groupKey ?? "");
        setMinVolume(String(initial.minVolume ?? HOUR_DEFAULT_MIN_VOLUME));
      } else {
        setName("");
        setColor("");
        setEmoji("");
        setSingleSourceOnly(false);
        setListSystemTags([]);
        setConditions([{ field: "profit", operator: "gte", value: 100 }]);
        if (defaultRange) {
          setFromDate(defaultRange.from);
          setToDate(defaultRange.to);
        }
        setToDateIsToday(false);
        setEnabled(true);
        setGroupKey("");
        setMinVolume(String(HOUR_DEFAULT_MIN_VOLUME));
      }
    }
  }, [open, initial?.id, defaultRange, initial]);

  const addCondition = useCallback(() => {
    setConditions((prev) => [
      ...prev,
      { field: "profit", operator: "gte", value: 0 },
    ]);
  }, []);

  const updateCondition = useCallback((idx: number, c: TagRuleCondition) => {
    setConditions((prev) => {
      const next = [...prev];
      next[idx] = c;
      return next;
    });
  }, []);

  const removeCondition = useCallback((idx: number) => {
    setConditions((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const handleSubmit = async () => {
    if (!name.trim()) return;
    if (conditions.length === 0) return;
    if (!fromDate.trim()) return;
    if (!toDateIsToday && !toDate.trim()) return;
    if (isHour && minVolumeValue === null) return;
    const derived = toDateIsToday
      ? "custom"
      : getPresetFromRange(fromDate, toDate);
    // Hours: a rolling preset is saved as the same whole days.
    const preset = isHour ? toHourPreset(derived) : derived;
    const sanitizedConditions = conditions.map((c) => {
      const countries =
        c.countryThresholds
          ?.map((t) => ({ ...t, countryKey: t.countryKey.trim().toLowerCase() }))
          .filter((t) => t.countryKey.length > 0) ?? [];
      // Drop the blank draft row each picker keeps while the user chooses.
      const campaigns =
        c.campaignThresholds
          ?.map((t) => ({ ...t, campaignId: t.campaignId.trim() }))
          .filter((t) => t.campaignId.length > 0) ?? [];
      const { countryThresholds: _c, campaignThresholds: _cm, ...rest } = c;
      return {
        ...rest,
        ...(countries.length ? { countryThresholds: countries } : {}),
        ...(campaigns.length ? { campaignThresholds: campaigns } : {}),
      };
    });
    await onSubmit({
      name: name.trim(),
      color: color.trim() || undefined,
      // "" clears the emoji on update; only drop rules carry one.
      // "" / [] clear on update; only drop rules carry these.
      ...(variant === "drop" ? { emoji, singleSourceOnly, listSystemTags } : {}),
      // List rules share the single-source gate (one level up: the list
      // itself must be single-sourced), nothing else of the drop extras.
      ...(variant === "list" ? { singleSourceOnly } : {}),
      ...(isHour ? { minVolume: minVolumeValue ?? HOUR_DEFAULT_MIN_VOLUME } : {}),
      conditions: sanitizedConditions,
      dateRangePreset: preset,
      fromDate,
      toDate: toDateIsToday ? undefined : toDate,
      toDateIsToday: toDateIsToday || undefined,
      enabled,
      groupKey: groupKey.trim() || undefined,
    });
    onOpenChange(false);
  };

  if (!open) return null;

  // Portalled on purpose — inline it renders inside the drawer's transformed,
  // overflow-hidden panel and gets clipped. Target the drawer's modal surface
  // when one is published (so its FocusScope keeps the inputs typeable), else
  // <body>. `pointer-events-auto` is the second half: Radix sets
  // `pointer-events: none` on <body> while the drawer is open.
  return createPortal(
    <AnimatePresence>
      <motion.div
        className="pointer-events-auto fixed inset-0 z-[200]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <div
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={() => onOpenChange(false)}
          aria-hidden="true"
        />
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.98 }}
          transition={{ duration: 0.2 }}
          className="absolute inset-0 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="rule-form-title"
        >
          <Card
            className="flex max-h-[90vh] w-full max-w-2xl flex-col shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b bg-muted/30 px-6 py-4">
              <h2 id="rule-form-title" className="text-lg font-semibold">
                {isEdit ? "Edit rule" : "Create rule"}
              </h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onOpenChange(false)}
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto">
              <div className="space-y-5 px-6 py-5">
                <div>
                  <label className="block text-sm font-medium">Tag name</label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Label applied when all conditions pass
                  </p>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. High Profit"
                    className="mt-2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium">
                    Group (optional)
                  </label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Rules with the same group are mutually exclusive — first
                    match wins. Drag rules within a group to set evaluation
                    order (top = checked first).
                  </p>
                  <CreatableCombobox
                    options={availableGroupKeys}
                    value={groupKey}
                    onChange={setGroupKey}
                    placeholder="e.g. profit-tier"
                    createLabel={(v) => `Create "${v}"`}
                    className="mt-2 max-w-xs"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium">Tag color</label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Badge color shown next to the list. Click to open palette.
                  </p>
                  <div className="mt-2 max-w-[200px]">
                    <ColorPicker
                      color={color || ""}
                      onChange={setColor}
                      placeholder="#22c55e"
                      presets={TAG_COLOR_PRESETS}
                    />
                  </div>
                </div>

                {variant === "drop" && (
                  <div>
                    <label className="block text-sm font-medium">Emoji</label>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Shown on drop rows. Rules with a Source * or List *
                      condition show only the emoji (bare, no badge); other
                      rules show it next to the tag name.
                    </p>
                    <div className="mt-2 flex gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant={emoji === "" ? "default" : "outline"}
                        onClick={() => setEmoji("")}
                      >
                        None
                      </Button>
                      {TAG_RULE_EMOJIS.map((e) => (
                        <Button
                          key={e}
                          type="button"
                          size="sm"
                          variant={emoji === e ? "default" : "outline"}
                          className="w-10 font-bold"
                          onClick={() => setEmoji(e)}
                        >
                          {e}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}

                {(variant === "drop" || variant === "list") && (
                  <div>
                    <label className="block text-sm font-medium">
                      Single source
                    </label>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {variant === "list"
                        ? '"Single source only" applies the rule only to lists with exactly one source (in the List > Source tree). "All" applies it to every list.'
                        : '"Single source only" applies the rule only to drops related to a single source (their list has exactly one source). "All" applies it to every drop.'}
                    </p>
                    <div className="mt-2 flex gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant={!singleSourceOnly ? "default" : "outline"}
                        onClick={() => setSingleSourceOnly(false)}
                      >
                        All
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant={singleSourceOnly ? "default" : "outline"}
                        onClick={() => setSingleSourceOnly(true)}
                      >
                        Single source only
                      </Button>
                    </div>
                  </div>
                )}

                {variant === "drop" && (
                  <div>
                    <label className="block text-sm font-medium">
                      List system tag
                    </label>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Apply the rule only to drops whose list currently has
                      the selected system tag (per the system tags
                      configuration). Pick one or both; "Any" applies the
                      rule regardless of system tags.
                    </p>
                    <div className="mt-2 flex gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant={
                          listSystemTags.length === 0 ? "default" : "outline"
                        }
                        onClick={() => setListSystemTags([])}
                      >
                        Any
                      </Button>
                      {DROP_RULE_LIST_SYSTEM_TAGS.map((tag) => (
                        <Button
                          key={tag}
                          type="button"
                          size="sm"
                          variant={
                            listSystemTags.includes(tag)
                              ? "default"
                              : "outline"
                          }
                          onClick={() =>
                            setListSystemTags((prev) =>
                              prev.includes(tag)
                                ? prev.filter((t) => t !== tag)
                                : [...prev, tag],
                            )
                          }
                        >
                          {tag}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="block text-sm font-medium">
                        Conditions
                      </label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        All must pass (AND logic). Same metrics as SMSWiser list
                        stats.
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addCondition}
                    >
                      <Plus className="mr-1.5 h-4 w-4" />
                      Add condition
                    </Button>
                  </div>
                  {/* Once, above every condition — the scope is the same for
                      all of them, so repeating it per row was pure noise. */}
                  {!isDefaultScope(scope) && scope ? (
                    <div className="mt-3">
                      <ScopeBanner label={labelFor(scope)} />
                    </div>
                  ) : null}
                  <CompareWindowExclusions value={excludedPresets}>
                    <div className="mt-3 space-y-2">
                      {conditions.map((c, i) => (
                        <ConditionModeRow
                          key={i}
                          condition={c}
                          onChange={(nc) => updateCondition(i, nc)}
                          onRemove={() => removeCondition(i)}
                          canRemove={conditions.length > 1}
                          scope={scope}
                          fields={metricFields}
                        />
                      ))}
                    </div>
                  </CompareWindowExclusions>
                </div>

                <details className="group rounded-lg border border-border bg-muted/10">
                  <summary className="flex cursor-pointer list-none items-center justify-between px-4 py-2.5 text-sm text-muted-foreground transition-colors hover:text-foreground [&::-webkit-details-marker]:hidden">
                    <span className="font-medium text-foreground">
                      Available metrics
                    </span>
                    <ChevronDown className="h-4 w-4 shrink-0 transition-transform group-open:rotate-180" />
                  </summary>
                  <div className="border-t border-border px-4 py-3">
                    <p className="mb-2 text-xs text-muted-foreground">
                      Same columns as Lists stats tab.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {metricFields.map((f) => (
                        <span
                          key={f.key}
                          className="inline-flex rounded-md bg-background px-2.5 py-1 text-xs font-medium text-foreground ring-1 ring-border"
                        >
                          {f.label}
                        </span>
                      ))}
                    </div>
                  </div>
                </details>

                {isHour && (
                  <HourMinVolumeField
                    value={minVolume}
                    onChange={setMinVolume}
                    invalid={minVolumeValue === null}
                  />
                )}

                <div>
                  <label className="block text-sm font-medium">
                    Date range
                  </label>
                  <p className="text-xs text-muted-foreground mt-0.5 mb-2">
                    {isHour
                      ? "Whole days on the recipients' clock"
                      : "Period used to fetch list stats"}
                  </p>
                  <DateRangePresetDropdown
                    fromDate={fromDate}
                    toDate={toDate}
                    onFromChange={setFromDate}
                    onToChange={setToDate}
                    showToDateIsToday
                    toDateIsToday={toDateIsToday}
                    onToDateIsTodayChange={setToDateIsToday}
                    excludePresets={excludedPresets}
                  />
                </div>

                <div
                  className={cn(
                    "flex items-center gap-3 rounded-lg border border-border p-3",
                  )}
                >
                  <label className="flex cursor-pointer items-center gap-2.5">
                    <Switch checked={enabled} onCheckedChange={setEnabled} />
                    <span
                      className={cn(
                        "text-sm",
                        enabled ? "font-medium" : "text-muted-foreground",
                      )}
                    >
                      {enabled ? "Rule enabled" : "Rule disabled"}
                    </span>
                  </label>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 border-t bg-muted/20 px-6 py-4">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={
                  submitting || !name.trim() || (isHour && minVolumeValue === null)
                }
              >
                {submitting
                  ? "Saving…"
                  : isEdit
                    ? "Save changes"
                    : "Create rule"}
              </Button>
            </div>
          </Card>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    portalHost ?? document.body,
  );
};
